import { useCallback, useEffect, useRef, useState } from "react";

import {
  ask as askApi,
  askConfig,
  askEstimate,
  askThread,
  clearAskThread,
  deleteLastAskMessage,
  type AskConfig,
  type AskEstimate,
  type AskReference,
} from "@/lib/ask";

import {
  IDLE,
  addChip,
  applyFrame,
  removeChip,
  toRows,
  type AskRow,
  type LiveTurn,
} from "./askTranscript";

const MODE_KEY = "nihongonator.read.dockMode";

export type DockMode = "lookup" | "ask";

/**
 * The dock's mode, and Ask's state for the log in front of you (日本-325).
 *
 * **The mode is per device and stays out of `NavState`.** Per 日本-321 a segment toggle is churn
 * rather than a place: an entry per switch buries the one that leaves the app, and a mode restored
 * from a shared URL would put someone else's dock in front of you. `localStorage`, beside
 * `logTabs.ts`, for the same reason tab order lives there.
 *
 * **The draft and the chips are per log**, cleared when the log changes: a question half-typed
 * about 第三章 makes no sense pointed at a news article, and chips referencing sentences that are
 * no longer on screen would be worse than none.
 */
export function useAsk(
  logId: string | null,
  onTranslation: (sentenceId: string, translation: string) => void,
) {
  const [mode, setMode] = useState<DockMode>(() => readMode());
  const [config, setConfig] = useState<AskConfig | null>(null);
  const [stored, setStored] = useState<AskRow[]>([]);
  const [live, setLive] = useState<LiveTurn>(IDLE);
  const [chips, setChips] = useState<AskReference[]>([]);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<AskEstimate | null>(null);

  // The frame handler closes over the callback that repaints the log; a ref keeps it current
  // without making `send` re-create on every render of the surface above.
  const translation = useRef(onTranslation);
  translation.current = onTranslation;

  /**
   * **Every write a stream makes is guarded on the turn that started it.**
   *
   * An answer takes seconds and switching tabs during one is an ordinary gesture, so the request
   * outlives the log it belongs to. Unguarded, the old log's deltas accumulate into the new tab's
   * live turn and the post-stream refetch replaces the new log's stored rows with the old log's
   * whole conversation — the reader watching a conversation they are not having.
   *
   * A counter rather than a log id, because it also covers the narrower race within one log: the
   * `done` frame un-busies the composer before the refetch lands, so a second question can be sent
   * in that window and the first turn's `setLive(IDLE)` would wipe it off the screen.
   */
  const turn = useRef(0);
  const inFlight = useRef<AbortController | null>(null);

  useEffect(() => {
    void askConfig()
      .then(setConfig)
      // A deployment with no key answers `configured: false` rather than failing, so a *thrown*
      // error here means the route is unreachable — Ask hides itself either way.
      .catch(() => setConfig(null));
  }, []);

  useEffect(() => {
    // Abandon anything the previous log had in flight. Aborting as well as invalidating, so the
    // connection is released rather than left streaming an answer nobody will ever see.
    turn.current += 1;
    inFlight.current?.abort();
    inFlight.current = null;
    setLive(IDLE);
    setChips([]);
    setDraft("");
    setError(null);
    if (!logId) {
      setStored([]);
      return;
    }
    let cancelled = false;
    void askThread(logId)
      .then((thread) => {
        if (!cancelled) setStored(toRows(thread.messages));
      })
      .catch(() => {
        if (!cancelled) setStored([]);
      });
    return () => {
      cancelled = true;
    };
  }, [logId]);

  const choose = useCallback((next: DockMode) => {
    setMode(next);
    try {
      window.localStorage.setItem(MODE_KEY, next);
    } catch {
      // A private window with no storage is not a reason to refuse to switch segments.
    }
  }, []);

  const point = useCallback((reference: AskReference) => {
    setChips((current) => addChip(current, reference));
  }, []);

  const unpoint = useCallback((sentenceId: string) => {
    setChips((current) => removeChip(current, sentenceId));
  }, []);

  const send = useCallback(async () => {
    const question = draft.trim();
    if (!logId || !question || live.busy) return;
    const attached = chips;
    const mine = ++turn.current;
    const controller = new AbortController();
    inFlight.current?.abort();
    inFlight.current = controller;
    setDraft("");
    setChips([]);
    setError(null);
    // The question is drawn immediately, with its chips, rather than when the first token lands.
    // Anything else leaves the composer empty and the transcript unchanged for the length of a
    // round trip, which reads as the send having failed.
    setLive({
      rows: [
        { kind: "question", id: `live-q-${Date.now()}`, text: question, references: attached },
      ],
      busy: true,
      error: null,
      droppedTurns: 0,
    });
    try {
      await askApi(
        logId,
        question,
        attached,
        (frame) => {
          if (turn.current !== mine) return;
          if (frame.type === "translation") {
            translation.current(frame.sentenceId, frame.translation);
          }
          setLive((current) => applyFrame(current, frame));
        },
        controller.signal,
      );
    } catch (e) {
      if (turn.current !== mine) return;
      setLive((current) => ({ ...current, busy: false }));
      setError(e instanceof Error ? e.message : String(e));
      return;
    } finally {
      if (inFlight.current === controller) inFlight.current = null;
    }
    if (turn.current !== mine) return;
    // Fold the finished turn into the stored transcript and drop the live one, so there is exactly
    // one copy of it on screen. Read back rather than kept locally: the server assigned the real
    // message ids, and the next turn's `recall` refers to them.
    void askThread(logId)
      .then((thread) => {
        if (turn.current !== mine) return;
        setStored(toRows(thread.messages));
        setLive(IDLE);
      })
      .catch(() => undefined);
  }, [chips, draft, live.busy, logId]);

  /**
   * Stop the answer in flight.
   *
   * **Aborting the request is the whole mechanism, and it reaches the model.** Killing the fetch
   * closes the response body, which raises inside the server's stream generator; the gateway's
   * `httpx` stream unwinds with it and the upstream connection is dropped, so a stopped answer
   * stops being generated rather than merely stopping being drawn. The client's own log records it
   * as cancelled, with everything that had arrived by then.
   *
   * The turn counter is bumped as well as the controller aborted, because `send`'s `finally` and
   * its post-stream refetch both run on the way out of an aborted request — without the bump the
   * refetch would land and replace the transcript a moment after the reader stopped it.
   *
   * What was streamed is **kept on screen**, not rolled back. The server persisted every message as
   * it was produced, so a partial answer is a real row; discarding it here would show less than the
   * database holds and less than the reader already read.
   */
  const stop = useCallback(() => {
    if (!inFlight.current) return;
    turn.current += 1;
    inFlight.current.abort();
    inFlight.current = null;
    setLive((current) => ({ ...current, busy: false }));
  }, []);

  /**
   * What the next request would cost, refreshed as the question is typed.
   *
   * **Debounced, and every in-flight estimate is abandoned when the next keystroke lands.** The
   * figure is computed server-side from the real assembled message list — which is what stops it
   * drifting from what is sent — so it is a round trip, and a request per character would be a
   * request per character. 400ms is long enough that typing a sentence costs one.
   *
   * It runs with an empty draft too, and deliberately: the transcript, the envelope and the tool
   * schemas are most of the bill before a word is typed, so the number is worth showing to a reader
   * who has not started.
   *
   * A failure clears the estimate rather than surfacing an error. This is a number beside a button;
   * a deployment that cannot produce it should draw nothing, not a red line above a working
   * composer.
   */
  useEffect(() => {
    if (!logId || !config?.configured || live.busy) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void askEstimate(logId, draft.trim(), chips, controller.signal)
        .then(setEstimate)
        .catch(() => {
          if (!controller.signal.aborted) setEstimate(null);
        });
    }, 400);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [logId, draft, chips, config?.configured, live.busy]);

  /**
   * Take back the end of the thread.
   *
   * **Not optimistic, unlike every other write in this surface.** The reading loop's writes are
   * hotkey-paced and must not stall, which is what that rule is for; this one is a deliberate
   * button press, and the unit the server removes is a *run* of rows rather than the one that was
   * clicked. Drawing a guess and correcting it a moment later would flicker the transcript through
   * a state the database never held — and guessing wrong the other way would leave a row on screen
   * that is no longer in the thread the next question will be built from.
   *
   * Guarded on the turn counter for `send`'s reason: the log can change while the request is out.
   */
  const deleteLast = useCallback(async () => {
    if (!logId || live.busy) return;
    const mine = turn.current;
    try {
      const thread = await deleteLastAskMessage(logId);
      if (turn.current !== mine) return;
      setStored(toRows(thread.messages));
      setLive(IDLE);
    } catch (e) {
      if (turn.current !== mine) return;
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [logId, live.busy]);

  const clear = useCallback(async () => {
    if (!logId) return;
    try {
      await clearAskThread(logId);
      setStored([]);
      setLive(IDLE);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [logId]);

  return {
    mode,
    setMode: choose,
    /** False on a deployment with no key — the segment is not drawn at all. */
    available: Boolean(config?.configured),
    model: config?.model ?? "",
    rows: [...stored, ...live.rows],
    busy: live.busy,
    error: error ?? live.error,
    droppedTurns: live.droppedTurns,
    chips,
    draft,
    setDraft,
    point,
    unpoint,
    send,
    stop,
    clear,
    deleteLast,
    estimate,
  };
}

function readMode(): DockMode {
  try {
    return window.localStorage.getItem(MODE_KEY) === "ask" ? "ask" : "lookup";
  } catch {
    return "lookup";
  }
}
